import { Inject, Injectable } from '@angular/core';
import { setIntersection } from '@sama/js-core';
import { get, isEmpty } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { merge, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  getShapeForEventTracking,
  ShapeState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import {
  MouseEventService,
  SubpointEventInfo,
} from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { ensureKeyLocation, LocationHelper } from '../../utils/location-helper';
import { INCREMENTAL_SHAPE_TOOL_NAMES } from '../shared/tool-helpers/incremental-shape-tool-helper';
import { ImageBoundsService } from './image-bounds.service';

@Injectable()
export class AddRemoveModeHelperService extends InjectableSuperclass {
  subpointAdded$ = new Subject<SubpointEventInfo>();

  constructor(
    private mouseEventService: MouseEventService,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private store: AbstractSensorContentStore2D,
    private browserControlService: BrowserControlService,
    private imageBoundsService: ImageBoundsService,
  ) {
    super();
  }

  setUpSubscriptions() {
    const containerMouseDown$ = merge(
      this.mouseEventService.getEvent$('container', 'mousedown'),
      this.mouseEventService.getEvent$('container', 'mousedown', {
        modifiers: ['shiftKey'],
      }),
    );

    this.subscribeTo(
      containerMouseDown$.pipe(filter(() => this.isInAddMode())),
      this.addSingleClickSubpoint,
    );
  }

  canSelectShape(event: MouseEvent) {
    return (
      this.isInAddMode() &&
      (event[this.browserControlService.getPlatformModifier()] ||
        isEmpty(this.store.state().selection))
    );
  }

  canDragSubpoint(eventInfo: SubpointEventInfo) {
    return (
      this.canSelectShape(eventInfo.event) ||
      (this.isInAddMode() &&
        this.store.state().selection[eventInfo.shapeStore.state().index])
    );
  }

  allowIncrementalShapeToolModes() {
    const output = this.store.state().output;
    const availableTools = new Set(get(output, ['meta', 'available_tools']));
    return (
      setIntersection(new Set(INCREMENTAL_SHAPE_TOOL_NAMES), availableTools)
        .size > 0
    );
  }

  private addSingleClickSubpoint() {
    const focusedShapeStore = this.store.getFocusedShapeStore();
    if (!focusedShapeStore) {
      return;
    }

    const shapeHelper = getShapeHelper(focusedShapeStore.state().type);
    if (!shapeHelper.allowsAddingAndRemovingSubpoints) {
      return;
    }

    const newPoint = this.imageBoundsService.boundPoints();
    const locationStore = ensureKeyLocation(
      new LocationHelper(this.store),
      focusedShapeStore('key_locations'),
      this.store.state().frameNumber,
    );
    const insertIndex = shapeHelper.getInsertIndex(
      locationStore.state().points,
      newPoint,
    );

    locationStore('points').mutateUsing(
      insertPointAtIndex,
      newPoint,
      insertIndex,
    );
    this.shellConnector.setShapes(this.store.state());
    this.trackEvent(focusedShapeStore.state());
  }

  private trackEvent(focusedShapeState: ShapeState2D) {
    this.shellConnector.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      method: 'add_mode',
      shapes: [getShapeForEventTracking(focusedShapeState)],
      source: 'edit_tool',
    });
  }

  private isInAddMode() {
    return this.store.state().editMode === 'add';
  }
}

function insertPointAtIndex(
  oldPoints: Point2D[],
  newPoint: Point2D,
  index: number,
) {
  oldPoints.splice(index, 0, newPoint);
}
